// 20260904 미디어수프 서버 통신 및 WebRTC 재생 관련 오류를 화면에 보여주기 위한
// 가벼운 인메모리 누적 로그. 서버 전송 없이 화면 표시 전용이며, dedupe 없이 발생한
// 오류를 전부 쌓아 최근 기록을 확인할 수 있게 한다. ... by claude

const MAX_ENTRIES = 200; // 무한정 쌓이지 않도록 상한만 둔다 (화면엔 최근 10건만 노출)

let entries = [];
let entrySeq = 0;
let hasUnread = false;
const listeners = new Set();

// 20260904 원본 errorCode를 사람이 이해할 6가지 카테고리로 정규화한다.
// 서버(media_soup/server)의 HandsupConsumeErrorCode와 클라이언트 자체 판별 코드를 함께 다룬다.
export const ErrorCategory = {
    CODEC: "CODEC",
    PLAYBACK: "PLAYBACK",
    CONNECTION: "CONNECTION",
    SERVER: "SERVER",
    DEVICE: "DEVICE",
    UNKNOWN: "UNKNOWN"
};

const ERROR_CODE_CATEGORY_MAP = {
    // 코덱 협상 실패 (media_soup/server canConsumeForRequest)
    UNSUPPORTED_CODEC: ErrorCategory.CODEC,

    // 상대방이 아직 준비 안 됐거나 연결이 끊긴 경우
    PRODUCER_NOT_READY: ErrorCategory.CONNECTION,
    PRODUCER_NOT_FOUND: ErrorCategory.CONNECTION,
    CONSUMER_GONE: ErrorCategory.CONNECTION,
    TRANSPORT_FAILED: ErrorCategory.CONNECTION,
    TRANSPORT_DISCONNECTED: ErrorCategory.CONNECTION,
    TRANSPORT_CLOSED: ErrorCategory.CONNECTION,

    // 실제 재생(디코딩) 중 실패 - <video>/<audio> MediaError
    MEDIA_ERR_ABORTED: ErrorCategory.PLAYBACK,
    MEDIA_ERR_NETWORK: ErrorCategory.PLAYBACK,
    MEDIA_ERR_DECODE: ErrorCategory.PLAYBACK,
    MEDIA_ERR_SRC_NOT_SUPPORTED: ErrorCategory.PLAYBACK,
    PLAYBACK_FAILED: ErrorCategory.PLAYBACK, // video.play()/audio.play() 자체가 거부된 경우 (resumeAfter)

    // 서버 통신/내부 처리 오류 (media_soup/server HandsupConsumeErrorCode 나머지)
    INVALID_REQUEST: ErrorCategory.SERVER,
    HANDSUP_INFO_NOT_FOUND: ErrorCategory.SERVER,
    SENDER_INFO_NOT_FOUND: ErrorCategory.SERVER,
    ROOM_INFO_NOT_FOUND: ErrorCategory.SERVER,
    ROOM_RECEIVER_NOT_FOUND: ErrorCategory.SERVER,
    CONSUMER_TRANSPORT_NOT_FOUND: ErrorCategory.SERVER,
    ROUTER_NOT_FOUND: ErrorCategory.SERVER,
    RECEIVER_MAP_NOT_FOUND: ErrorCategory.SERVER,
    RECEIVER_INFO_NOT_FOUND: ErrorCategory.SERVER,
    CREATE_CONSUMER_FAILED: ErrorCategory.SERVER,
    UNEXPECTED_SERVER_ERROR: ErrorCategory.SERVER,

    // 기기/브라우저(mediasoup-client Device) 호환성 문제
    UnsupportedError: ErrorCategory.DEVICE,
    InvalidStateError: ErrorCategory.DEVICE,
    TypeError: ErrorCategory.DEVICE
};

// 20260904 <video>/<audio> 엘리먼트의 MediaError.code(숫자)를 문자열 코드로 변환한다.
export const videoErrorCodeToString = code => {
    switch (code) {
        case 1: return "MEDIA_ERR_ABORTED";
        case 2: return "MEDIA_ERR_NETWORK";
        case 3: return "MEDIA_ERR_DECODE";
        case 4: return "MEDIA_ERR_SRC_NOT_SUPPORTED";
        default: return "UNKNOWN";
    }
};

export const getErrorCategory = errorCode => ERROR_CODE_CATEGORY_MAP[errorCode] || ErrorCategory.UNKNOWN;

// 카테고리별 "원인" 번역 키 (ns: "live")
export const CATEGORY_REASON_KEY = {
    [ErrorCategory.CODEC]: "오류_원인_코덱",
    [ErrorCategory.PLAYBACK]: "오류_원인_재생실패",
    [ErrorCategory.CONNECTION]: "오류_원인_연결끊김",
    [ErrorCategory.SERVER]: "오류_원인_서버오류",
    [ErrorCategory.DEVICE]: "오류_원인_기기호환",
    [ErrorCategory.UNKNOWN]: "오류_원인_알수없음"
};

const notify = () => {
    const snapshot = getSnapshot();
    listeners.forEach(listener => {
        try {
            listener(snapshot);
        } catch (err) {
            console.log("[MediaErrorLog] listener error => ", err);
        }
    });
};

/**
 * 오류 1건을 누적 로그에 기록한다. dedupe 없이 발생한 만큼 전부 쌓인다.
 *
 * @param {Object} info
 * @param {String} info.roomKind - "live" | "handsup" 등
 * @param {String} info.mediaKind - "video" | "audio" | "mic"
 * @param {Number} [info.prodUserSeq] - 오류가 발생한 producer(발신자) 시퀀스
 * @param {Number} [info.consumUserSeq] - 수신자(나) 시퀀스
 * @param {String} info.errorCode - 원본 오류 코드 (디버그/카테고리 판별용)
 * @param {Array}  [info.producerCodecs] - 코덱 관련 오류일 때 서버가 보내주는 mimeType 배열
 */
export const addMediaError = info => {
    if (!info || !info.errorCode) return;

    entrySeq += 1;
    const entry = {
        id: entrySeq,
        time: new Date(),
        roomKind: info.roomKind,
        mediaKind: info.mediaKind,
        prodUserSeq: info.prodUserSeq,
        consumUserSeq: info.consumUserSeq,
        errorCode: info.errorCode,
        category: getErrorCategory(info.errorCode),
        producerCodecs: info.producerCodecs
    };

    entries.push(entry);
    if (entries.length > MAX_ENTRIES) entries = entries.slice(entries.length - MAX_ENTRIES);
    hasUnread = true;
    notify();
};

// 최근 n건을 최신순으로 반환한다 (기본 10건).
export const getRecentMediaErrors = (count = 10) => entries.slice(-count).reverse();

export const getMediaErrorCount = () => entries.length;

export const hasUnreadMediaError = () => hasUnread;

// 벨을 클릭해 로그를 열람하는 시점에 호출 - 깜빡임만 멈추고 누적된 건수/기록은 유지한다.
export const markMediaErrorsRead = () => {
    if (!hasUnread) return;
    hasUnread = false;
    notify();
};

export const getSnapshot = () => ({
    count: entries.length,
    hasUnread
});

export const subscribeMediaErrorLog = listener => {
    listeners.add(listener);
    listener(getSnapshot());
    return () => listeners.delete(listener);
};
